import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "@/components/hooks/useAuth";
import { PERMISSIONS } from "@/utils/permissions";
import { getLogger } from "@/utils/logger";

const log = getLogger("ProfileWarning");
const TOAST_ID = "profile-completion-warning";
const CHECK_INTERVAL_MS = 120000;

/**
 * Shows a profile completion toast on all pages for authenticated accounts.
 */
const ProfileWarning = () => {
  const { isAuthenticated, isLoading, auth0User, userRoles = [], userPermissions = [] } = useAuth();
  const location = useLocation();
  const normalizedRoles = userRoles
    .filter((role) => typeof role === "string")
    .map((role) => role.trim().toLowerCase());
  const canSeeProfileWarning =
    userPermissions.includes(PERMISSIONS.ACCESS_ADMIN) || normalizedRoles.includes("admin");

  const checkProfileCompletion = useCallback(
    (trigger) => {
      if (isLoading || !isAuthenticated || !auth0User?.sub) return;
      if (!canSeeProfileWarning) return;
      if (location.pathname.startsWith("/profile")) return;
      if (!auth0User?.needsProfileCompletion) return;

      log.info(
        `[ProfileWarning:checkProfileCompletion] > [Request]: evaluate profile state > [Response]: needsCompletion=true > [Comment]: trigger=${trigger}`
      );

      if (toast.isActive(TOAST_ID)) return;

      toast.error(
        <div className="space-y-2 text-black">
          <div className="font-semibold">Complete your profile</div>
          <div className="text-sm">
            Your profile is missing required details.
          </div>
          <a
            href="/profile"
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
            id='profile-warning-link-update-profile'>
            Update Profile
          </a>
        </div>,
        {
          toastId: TOAST_ID,
          autoClose: 10000,
          position: "bottom-right",
          className: "border border-red-200 bg-red-50",
          progressClassName: "bg-red-600",
        }
      );
    },
    [
      canSeeProfileWarning,
      isAuthenticated,
      isLoading,
      auth0User?.sub,
      auth0User?.needsProfileCompletion,
      location.pathname,
    ]
  );

  useEffect(() => {
    checkProfileCompletion("navigation");
  }, [location.pathname, checkProfileCompletion]);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return undefined;
    const intervalId = setInterval(() => {
      checkProfileCompletion("interval");
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, isLoading, checkProfileCompletion]);

  return null;
};

export default ProfileWarning;
